import React, { useRef, useState, useCallback } from 'react';
import Photo from './photo';
import classNames from 'classnames';
import I18n from 'i18n-js';
import { get, includes } from 'lodash';
import { camelizeKeys } from 'humps';

import { useExpEnabled } from '@reverbdotcom/commons/src/user_hooks';
import experiments from '@reverbdotcom/commons/src/experiments';

interface IProps {
  uploadOptions: any;
  onFileAdded: Function;
  onUploadFailure?: Function;
  onDragStart: Function; // unused
  onDragEnd: Function; // unused
  dropZoneSelector?: string; // unused
  thumbMessage?: string;
}

import { uploadFiles, CREATE_IMAGE_UPLOAD_URL_MUTATION } from '@reverbdotcom/commons/src/components/PhotoUploader/uploadFile';
import { useMutation } from '@reverbdotcom/commons/src/useMutation';

const ACCEPTED_FILE_TYPES = [
  'image/jpeg',
  'image/png',
];

const Uploader: React.FC<IProps> = ({
  uploadOptions,
  onFileAdded,
  onUploadFailure = () => {},
  thumbMessage,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);

  const isUploadToS3Enabled = useExpEnabled(experiments.UPLOAD_IMAGES_CLIENT_SIDE_TO_S3);
  const [createImageUploadUrlMutation] = useMutation(CREATE_IMAGE_UPLOAD_URL_MUTATION);

  const handleFileAdded = useCallback(async (acceptedFiles: File[]) => {
    setIsUploading(true);

    const uploadFileResults = await uploadFiles(acceptedFiles, createImageUploadUrlMutation, isUploadToS3Enabled);

    uploadFileResults.forEach((uploadFileResult) => {
      if (!uploadFileResult) {
        onUploadFailure();
      } else {
        onFileAdded(new Photo(camelizeKeys(uploadFileResult)));
      }
    });

    setIsUploading(false);
  }, [
    onUploadFailure,
    uploadFiles,
    createImageUploadUrlMutation,
    isUploadToS3Enabled,
    setIsUploading,
  ]);

  const handleInputChange = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.currentTarget.files || []);

    const invalidFiles = files.filter(f => !includes(ACCEPTED_FILE_TYPES, f.type));
    if (invalidFiles.length > 0) {
      onUploadFailure?.();
      return;
    }

    try {
      await handleFileAdded(files);
    } catch (error) {
      onUploadFailure?.();
    }
  }, [handleFileAdded, onUploadFailure]);

  const renderLabel = () => {
    if (isUploading) {
      return I18n.t('discovery.photos.uploader.inProgress');
    }
    return thumbMessage || I18n.t('discovery.photos.uploader.uploadPhotos');
  };

  const className = classNames(
    'img-uploader__thumb',
    'img-uploader__thumb--add-photo',
    { 'img-uploader__thumb--add-photo--active': !isUploading },
  );

  return (
    <div className={className}>
      <div className="img-uploader__thumb__message">
        <label>{renderLabel()}</label>
      </div>
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPTED_FILE_TYPES.join(',')}
        multiple={get(uploadOptions, 'html.multiple')}
        onChange={handleInputChange}
      />
    </div>
  );
};

export default Uploader;
